import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Button } from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export const useCheckUpdate = (options = {}) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const {
		interval = 60_000 * 60 * 12, // 12 hours
		enabled = true,
		autoReload = true,
		maxRetries = 3,
		retryDelay = 5000,
	} = options;

	const intervalRef = useRef(null);
	const isCheckingRef = useRef(false);
	const retryCountRef = useRef(0);
	const lastReloadTimeRef = useRef(0);

	const snackbarAction = () => (
		<>
			<Button
				size="small"
				variant="contained"
				onClick={() => {
					window.location.reload();
				}}
			>
				{t("refresh_now")}
			</Button>
		</>
	);

	const fetchVersion = useCallback(async () => {
		// Prevent concurrent checks
		if (isCheckingRef.current) return;

		// Avoid reload loop - don't check too soon after a reload
		const timeSinceLastReload = Date.now() - lastReloadTimeRef.current;
		if (timeSinceLastReload < 10000) {
			// 10 seconds minimum
			return;
		}

		isCheckingRef.current = true;

		try {
			// Cache-busting with timestamp
			const cacheBuster = Date.now();
			const response = await fetch(`/version.json?v=${cacheBuster}`, {
				cache: "no-cache",
				headers: {
					"Cache-Control": "no-cache, no-store, must-revalidate",
					Pragma: "no-cache",
					Expires: "0",
				},
			});

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const data = await response.json();
			const currentVersion = data.version;

			if (!currentVersion) {
				throw new Error("Version field not found in response");
			}

			const savedVersion = localStorage.getItem("appVersion");

			console.log("Version check:", {
				current: currentVersion,
				saved: savedVersion,
			});

			// Reset retry count on successful fetch
			retryCountRef.current = 0;

			if (savedVersion && savedVersion !== currentVersion) {
				console.log("New version detected:", currentVersion);

				// Store the new version
				localStorage.setItem("appVersion", currentVersion);

				if (autoReload) {
					lastReloadTimeRef.current = Date.now();
					// Small delay to allow logging to complete
					setTimeout(() => {
						enqueueSnackbar(
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
								<span>{t("new_update_detected_info")}</span>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<WarningAmberIcon /> {t("new_update_warning_info")}
								</Box>
							</Box>,
							{
								variant: "info",
								preventDuplicate: true,
								action: snackbarAction,
								anchorOrigin: {
									vertical: "bottom",
									horizontal: "right",
								},
								autoHideDuration: null,
								persist: true,
								hideIconVariant: true,
							}
						);
					}, 100);
				}
			} else if (!savedVersion) {
				// First time - just save the version without reloading
				localStorage.setItem("appVersion", currentVersion);
				console.log("Initial version saved:", currentVersion);
			}
		} catch (error) {
			console.error("Failed to fetch version:", error);

			// Increment retry count
			retryCountRef.current += 1;

			// If we've exceeded max retries, stop the interval
			if (retryCountRef.current >= maxRetries) {
				console.warn(
					`Version check failed ${maxRetries} times. Stopping automatic checks.`
				);
				if (intervalRef.current) {
					clearInterval(intervalRef.current);
					intervalRef.current = null;
				}
			} else {
				// Schedule a retry with delay
				setTimeout(() => {
					if (enabled) {
						fetchVersion();
					}
				}, retryDelay);
			}
		} finally {
			isCheckingRef.current = false;
		}
	}, [autoReload, maxRetries, retryDelay]);

	const startChecking = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}

		// Initial check
		fetchVersion();

		// Set up interval for periodic checks
		intervalRef.current = setInterval(fetchVersion, interval);
	}, [fetchVersion, interval]);

	const stopChecking = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, []);

	useEffect(() => {
		if (enabled) {
			startChecking();
		} else {
			stopChecking();
		}

		// Cleanup on unmount
		return () => {
			stopChecking();
		};
	}, [enabled, startChecking, stopChecking]);

	// Manual check function
	const checkNow = useCallback(() => {
		fetchVersion();
	}, [fetchVersion]);

	return {
		checkNow,
		isChecking: isCheckingRef.current,
		retryCount: retryCountRef.current,
		startChecking,
		stopChecking,
	};
};
